import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActivities, getWorkspaceMembers } from '../services/api';
import AzotteGrid from '../components/AzotteGrid';
import {
  Box, Typography, TextField, Stack, Grid, Chip, Alert, MenuItem, CircularProgress,
  Select, Checkbox, ListItemText, FormControl, InputLabel, Button, OutlinedInput,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';

const DIRECTIONS = ['Outbound', 'Inbound', 'InternalNote'];
const CHANNELS = ['Email', 'LinkedInManual', 'Call', 'Meeting', 'Note'];
const STATUSES = ['Draft', 'Scheduled', 'ReadyToSend', 'Sent', 'Delivered', 'Opened', 'Replied', 'Failed', 'Archived'];
const STAGES = ['FirstContact', 'FollowUp', 'Qualification', 'ReEngagement', 'DemoInvite', 'Negotiation', 'Closed'];

const directionColor = (d) => d === 'Inbound' ? 'success' : d === 'Outbound' ? 'primary' : 'default';
const statusColor = (s) => {
  if (s === 'Replied' || s === 'Delivered' || s === 'Opened') return 'success';
  if (s === 'Failed') return 'error';
  if (s === 'Sent' || s === 'ReadyToSend') return 'info';
  if (s === 'Archived') return 'default';
  return 'warning';
};

const renderMultiValue = (selected) =>
  !selected || selected.length === 0 ? 'All' : `${selected.length} selected`;

const menuProps = { PaperProps: { sx: { maxHeight: 360 } } };

export default function ActivityListPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);

  const [search, setSearch] = useState('');
  const [userIds, setUserIds] = useState([]);
  const [directions, setDirections] = useState([]);
  const [channels, setChannels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [stages, setStages] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getWorkspaceMembers();
        setMembers(data || []);
      } catch {
        // members optional — filter still usable without
      }
    })();
  }, []);

  const queryParams = useMemo(() => {
    const p = {
      page: page + 1,
      pageSize: rowsPerPage,
    };
    if (search.trim()) p.search = search.trim();
    if (userIds.length) p.userIds = userIds;
    if (directions.length) p.directions = directions;
    if (channels.length) p.channels = channels;
    if (statuses.length) p.statuses = statuses;
    if (stages.length) p.stages = stages;
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;
    return p;
  }, [page, rowsPerPage, search, userIds, directions, channels, statuses, stages, dateFrom, dateTo]);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getActivities(queryParams);
      setItems(data?.items || []);
      setTotalCount(data?.totalCount || 0);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    const t = setTimeout(fetchActivities, 300);
    return () => clearTimeout(t);
  }, [fetchActivities]);

  const resetFilters = () => {
    setSearch('');
    setUserIds([]);
    setDirections([]);
    setChannels([]);
    setStatuses([]);
    setStages([]);
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  // Reset to page 0 whenever any filter changes
  useEffect(() => { setPage(0); }, [search, userIds, directions, channels, statuses, stages, dateFrom, dateTo]);

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

  const directionIcon = (d) => {
    if (d === 'Inbound') return <CallReceivedIcon fontSize="inherit" />;
    if (d === 'Outbound') return <CallMadeIcon fontSize="inherit" />;
    return <StickyNote2OutlinedIcon fontSize="inherit" />;
  };

  const columns = [
    {
      key: 'interactionDate',
      header: 'When',
      width: 170,
      render: (v) => <Typography variant="body2">{formatDate(v)}</Typography>,
    },
    {
      key: 'leadCompanyName',
      header: 'Lead',
      render: (v, row) => v
        ? <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.leadId}`); }}
          >{v}</Typography>
        : <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'contactName',
      header: 'Contact',
      hideOnMobile: true,
      render: (v) => v || <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'direction',
      header: 'Dir',
      width: 110,
      hideOnMobile: true,
      render: (v) => (
        <Chip
          icon={directionIcon(v)}
          label={v}
          size="small"
          color={directionColor(v)}
          variant={v === 'InternalNote' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      key: 'channel',
      header: 'Channel',
      width: 120,
      hideOnMobile: true,
      hideOnTablet: true,
      render: (v) => <Chip label={v} size="small" variant="outlined" />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (v) => <Chip label={v} size="small" color={statusColor(v)} variant="outlined" />,
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{v || '(no subject)'}</Typography>,
    },
    {
      key: 'createdByUserName',
      header: 'User',
      width: 140,
      hideOnMobile: true,
      render: (v, row) => v || row.conductedBy || <Typography color="text.disabled">-</Typography>,
    },
  ];

  const stats = [
    { label: 'Total', value: totalCount, color: 'primary.light', icon: <ChatBubbleOutlineOutlinedIcon /> },
    { label: 'Inbound', value: items.filter(i => i.direction === 'Inbound').length, color: 'success.light', icon: <CallReceivedIcon /> },
    { label: 'Outbound', value: items.filter(i => i.direction === 'Outbound').length, color: 'info.light', icon: <CallMadeIcon /> },
    { label: 'Notes', value: items.filter(i => i.direction === 'InternalNote').length, color: 'warning.light', icon: <StickyNote2OutlinedIcon /> },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Activities</Typography>
          <Typography variant="body2" color="text.secondary">All inbound &amp; outbound interactions across leads</Typography>
        </Box>
        <Button startIcon={<RestartAltIcon />} onClick={resetFilters}>Reset filters</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((s) => (
          <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: s.color }}>{s.icon}</Box>
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          <TextField
            placeholder="Search subject, body, notes, or company name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Users</InputLabel>
                <Select
                  multiple
                  value={userIds}
                  onChange={(e) => setUserIds(e.target.value)}
                  input={<OutlinedInput label="Users" />}
                  renderValue={renderMultiValue}
                  MenuProps={menuProps}
                >
                  {members.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Checkbox size="small" checked={userIds.includes(u.id)} />
                      <ListItemText primary={u.fullName || u.username} secondary={u.email} />
                    </MenuItem>
                  ))}
                  {members.length === 0 && <MenuItem disabled>No members available</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  multiple
                  value={directions}
                  onChange={(e) => setDirections(e.target.value)}
                  input={<OutlinedInput label="Direction" />}
                  renderValue={renderMultiValue}
                  MenuProps={menuProps}
                >
                  {DIRECTIONS.map((d) => (
                    <MenuItem key={d} value={d}>
                      <Checkbox size="small" checked={directions.includes(d)} />
                      <ListItemText primary={d} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  multiple
                  value={channels}
                  onChange={(e) => setChannels(e.target.value)}
                  input={<OutlinedInput label="Channel" />}
                  renderValue={renderMultiValue}
                  MenuProps={menuProps}
                >
                  {CHANNELS.map((c) => (
                    <MenuItem key={c} value={c}>
                      <Checkbox size="small" checked={channels.includes(c)} />
                      <ListItemText primary={c} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statuses}
                  onChange={(e) => setStatuses(e.target.value)}
                  input={<OutlinedInput label="Status" />}
                  renderValue={renderMultiValue}
                  MenuProps={menuProps}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Checkbox size="small" checked={statuses.includes(s)} />
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Stage</InputLabel>
                <Select
                  multiple
                  value={stages}
                  onChange={(e) => setStages(e.target.value)}
                  input={<OutlinedInput label="Stage" />}
                  renderValue={renderMultiValue}
                  MenuProps={menuProps}
                >
                  {STAGES.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Checkbox size="small" checked={stages.includes(s)} />
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }}>
              <TextField
                size="small"
                fullWidth
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }}>
              <TextField
                size="small"
                fullWidth
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <AzotteGrid
          columns={columns}
          rows={items}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          loading={loading}
          onDoubleClick={(row) => navigate(`/leads/${row.leadId}`)}
        />
      )}
    </Box>
  );
}
